<template>
  <details-header :loading="loading">
    <template v-slot:avatar>
      <image-edit-badge saveAction="company/uploadLogo" :disabled="!isAdmin">
        <avatar
          :avatarUrl="company.logoUrl"
          :size="80"
          :icon="!company.logoUrl ? 'city' : ''"
          :color="getColor($vuetify.theme.themes.light.secondarytext, 0.1)"
          iconColor="secondarytext"
        >
        </avatar>
      </image-edit-badge>
    </template>
    <template v-slot:headerLine1>
      {{ company.name }}
    </template>
    <template v-slot:headerLine2>
      Created on {{ company.createdDate | longDate }} |
      {{ company.userCount }} Members
    </template>
    <template v-slot:tabs>
      <v-tabs>
        <v-tab to="/company" exact>
          Company
        </v-tab>
        <v-tab to="/company/members">
          Members
        </v-tab>
        <v-tab to="/company/managers" v-if="settings.showManagers">
          Managers
        </v-tab>
        <v-tab to="/company/departments">
          Departments
        </v-tab>
        <v-tab to="/company/metrics">
          Metrics
        </v-tab>
        <v-tab to="/company/integrations" v-if="isAdmin">
          Integrations
        </v-tab>
      </v-tabs>
    </template>
    <v-row v-if="tab == ''">
      <v-col
        cols="12"
        v-if="settings.coreValues.length == 0 || !settings.missionStatement"
      >
        <ad-header-card
          title="Let's define your mission & core values 🧱"
          subtitle="Scroll down to add your mission statement and core values. We'll incorporate this information throughout your account."
        >
          <template v-slot:helpText>
            <div @click="goBack" class="cursor-pointer">
              <v-icon color="accent" small>keyboard_arrow_left</v-icon>
              Back to getting started
            </div>
          </template>
        </ad-header-card>
      </v-col>
      <v-col cols="12">
        <company-profile-form></company-profile-form>
      </v-col>
      <v-col cols="12" sm="4" class="py-0">
        <v-row>
          <v-col cols="12">
            <mission-statement-card></mission-statement-card>
          </v-col>
          <v-col cols="12" v-if="this.$vuetify.lang.current == 'table_group'">
            <strategic-anchor-card></strategic-anchor-card>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" sm="8">
        <core-values-card></core-values-card>
      </v-col>
    </v-row>
    <v-row v-if="tab == 'members'">
      <v-col cols="12" v-if="showCustomFields">
        <member-custom-field-list> </member-custom-field-list>
      </v-col>
      <template v-else-if="settings.isPaidPlan">
        <v-col cols="12" v-if="settings.isNonLitePlan">
          <v-card @click="showCustomFields = true">
            <v-card-text>
              <v-row dense>
                <v-col
                  cols="auto"
                  class="pl-8"
                  v-if="$vuetify.breakpoint.mdAndUp"
                >
                  <v-img :src="$images.MANAGERS_SETUP"></v-img>
                </v-col>
                <v-col>
                  <v-card-title>
                    Manage Custom Member fields
                  </v-card-title>
                  <v-card-text>
                    Want to add a birthday, personality type, or anything else
                    about your members?
                  </v-card-text>
                </v-col>
                <v-spacer class="limited-spacer" />
                <v-col cols="auto" class="my-auto">
                  <v-btn color="primary">
                    Manage Fields
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12">
          <member-list></member-list>
        </v-col>
      </template>
      <v-col cols="12" v-else>
        <ad-header-card
          title="Upgrade to organize your members 🔒"
          subtitle="A paid plan is required to manage your members. Upgrade and get more great features!"
        >
          <v-btn
            to="/billing"
            color="accent"
            outlined
            class="ml-4 background--white"
          >
            Upgrade to access
          </v-btn>
        </ad-header-card>
      </v-col>
    </v-row>
    <v-row v-if="tab == 'managers'">
      <v-col cols="12" v-if="settings.isNonLitePlan">
        <v-card>
          <v-card-text>
            <v-row dense>
              <v-col
                cols="auto"
                class="pl-8"
                v-if="$vuetify.breakpoint.mdAndUp"
              >
                <v-img :src="$images.MANAGERS_SETUP"></v-img>
              </v-col>
              <v-col>
                <v-card-title>
                  Turn your members into managers
                </v-card-title>
                <v-card-text>
                  Making someone a manager can be done by going to the
                  <router-link class="info--text" to="/company/members"
                    >members page </router-link
                  >. From there, work with your import spreadsheet or you can
                  define direct reports on each members profile page.
                </v-card-text>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12">
        <manager-list v-if="settings.isNonLitePlan"></manager-list>
        <ad-header-card
          v-else
          title="Upgrade to organize your people & your data 🔒"
          subtitle="Your current plan doesn't include manager or department functionality. Upgrade and get more great features!"
        >
          <v-btn
            to="/billing"
            color="accent"
            outlined
            class="ml-4 background--white"
          >
            Upgrade to access
          </v-btn>
          <span class="ml-4 accent--text">Standard or Pro plan required</span>
        </ad-header-card>
      </v-col>
    </v-row>
    <v-row v-if="tab == 'departments'">
      <v-col cols="12">
        <team-list v-if="settings.isNonLitePlan"></team-list>
        <ad-header-card
          v-else
          title="Upgrade to organize your people & your data 🔒"
          subtitle="Your current plan doesn't include manager or department functionality. Upgrade and get more great features!"
        >
          <v-btn
            to="/billing"
            color="accent"
            outlined
            class="ml-4 background--white"
          >
            Upgrade to access
          </v-btn>
          <span class="ml-4 accent--text">Standard or Pro plan required</span>
        </ad-header-card>
      </v-col>
    </v-row>
    <v-row v-if="tab == 'metrics'">
      <v-col cols="12">
        <business-metrics-list
          v-if="settings.isNonLitePlan"
        ></business-metrics-list>
        <ad-header-card
          v-else
          title="Upgrade & add integrated business metric reporting 🔒"
          subtitle="Visualize your business metrics within your favorite reports & spot trends in your culture that impact the metrics you watch"
        >
          <v-btn
            to="/billing"
            color="accent"
            outlined
            class="ml-4 background--white"
          >
            Upgrade to access
          </v-btn>
          <span class="ml-4 accent--text">Standard or Pro plan required</span>
        </ad-header-card>
      </v-col>
    </v-row>
    <v-row v-if="tab == 'integrations'">
      <v-col cols="12">
        <integration-list> </integration-list>
      </v-col>
    </v-row>
  </details-header>
</template>

<script>
import { mapGetters } from "vuex";
import CompanyProfileForm from "@/components/company/CompanyProfileForm";
import MemberList from "@/components/members/MemberList";
import ManagerList from "@/components/members/ManagerList";
import TeamList from "@/components/teams/TeamList";
import BusinessMetricsList from "@/components/metrics/BusinessMetricsList";
import DetailsHeader from "@/components/common/DetailsHeader";
import ImageEditBadge from "@/components/common/ImageEditBadge";
import MissionStatementCard from "@/components/company/MissionStatementCard";
import CoreValuesCard from "@/components/company/CoreValuesCard";
import StrategicAnchorCard from "@/components/company/StrategicAnchorCard";
import AdHeaderCard from "@/components/common/AdHeaderCard";
import IntegrationList from "@/components/company/IntegrationList";
import MemberCustomFieldList from "@/components/members/MemberCustomFieldList";
export default {
  name: "CompanyProfile",
  components: {
    CompanyProfileForm,
    DetailsHeader,
    ImageEditBadge,
    MemberList,
    ManagerList,
    TeamList,
    BusinessMetricsList,
    MissionStatementCard,
    CoreValuesCard,
    StrategicAnchorCard,
    AdHeaderCard,
    IntegrationList,
    MemberCustomFieldList
  },
  data: () => ({
    loading: false,
    tab: null,
    showCustomFields: false
  }),
  computed: {
    isAdmin() {
      return this.$auth.isAdmin();
    },
    ...mapGetters({
      core_values: "company/coreValues",
      company: "company/company",
      settings: "profile/settings"
    })
  },
  created() {
    if (!this.isAdmin) {
      this.$router.push({ path: "/team" });
      return;
    }

    if (!this._.isEmpty(this.company) && this.core_values.length > 0) {
      this.updateTab();
      return;
    }
    this.loading = true;
    Promise.all([
      this.$store.dispatch("company/getCompany"),
      this.$store.dispatch("company/getCoreValues"),
      this.$store.dispatch("company/getStrategicAnchors")
    ])
      .then(() => {
        this.updateTab();
      })
      .catch(error => {
        this.logError(error);
        this.$root.$alert(
          "Error Occurred",
          "An error occured while loading your company."
        );
      })
      .finally(() => {
        this.loading = false;
      });
  },
  methods: {
    updateTab() {
      this.tab = this.$route.params.tab || "";
      this.showCustomFields = false;
    },
    goBack() {
      this.$router.push({ path: "/" });
    }
  }
};
</script>
